import _ from 'underscore';
import { AuthenticationProviderSelectorModel } from '../../settings/siteAuthSettings/model/AuthenticationProviderSelectorModel';
import { ListResult } from '../fetchers/ListResult';
import { IActionContext } from '../helpers/ActionContext';
import { CommonErrorCode } from '../helpers/ErrorCodes';
import { L10n } from '../L10n/L10n';
import {
  IErrorItem,
  MultipleItemResult
} from '../../commonInfra/contentActions/MultipleItemResult';
import { Set } from '../models/Set';
import { Module } from '../Module';
import { SiteRolesResource } from '../../userManagement/services/resources/SiteRoles';
import { SiteUsers } from '../../userManagement/services/resources/SiteUsers';
import { IError } from '../../httpClient/serverApi/types/IError';
import { IResult } from '../../httpClient/serverApi/types/IResponse';
import { ISiteUser } from '../ServerApi/types/ISiteUser';
import { ConfirmActionDialog, IConfirmActionDialogInstance } from './ConfirmActionDialog';
import { ContentActionNotification} from '../../commonInfra/contentActions/ContentActionNotification';
import { ModalService } from './ModalService';
import {
  AuthenticationProviderType,
  IAuthenticationProvider,
  is3rdParty,
  SiteAuthenticationSettingsService,
} from './SiteAuthenticationSettingsService';
import { ServerSettingsUnauthenticated } from '../../settings/services/ServerSettingsUnauthenticated';
import { createMultipleItemResultNotificationValues } from '../../commonInfra/contentActions/ActionMessages';
import { AuthenticationProviderSelectorProps } from '../../../src/settings/siteAuthSettings/directives/AuthenticationProviderSelector';
import { getSessionManagementServiceInstance } from '../../sessionManagement/SessionManagementService';

export class UpdateUserAuthenticationAction {

  public static $inject = [
    '$q',
    '$translate',
    'SiteAuthenticationSettingsService',
    'ConfirmActionDialog',
  ];

  constructor(
    private $q: ng.IQService,
    private $translate: ng.translate.ITranslateService,
    private siteAuthenticationSettingsService: SiteAuthenticationSettingsService,
    private confirmActionDialog: ConfirmActionDialog
  ) { }

  public showUpdateUserAuthenticationDialog(
    siteUsersToUpdate: Set<ISiteUser>,
    context: IActionContext): ng.IPromise<any> {
    const scope = <IDialogScope> context.scope.$new();

    const getProviderDescription = (provider: IAuthenticationProvider) => {
      const translateValues = {
        SERVER_NAME: ServerSettingsUnauthenticated.serverName(),
        PROVIDER_NAME: this.siteAuthenticationSettingsService.getProviderDisplayName(provider),
      };
      let translateKey;
      switch(provider.type){
        case AuthenticationProviderType.default:
          translateKey = 'setUsersAuthentication_description_internal';
          break;
        case AuthenticationProviderType.tabidmfa:
          translateKey = 'setUsersAuthentication_description_tableauid_mfa';
          break;
        default:
          translateKey = 'setUsersAuthentication_description_external';

      }
      return this.$translate.instant(translateKey, translateValues);
    };
    const authModel = new AuthenticationProviderSelectorModel();
    this.siteAuthenticationSettingsService.getSiteAuthenticationSettings().then((settings) => {
      authModel.updateAuthenticationProvider(this.siteAuthenticationSettingsService.findProvider(settings, siteUsersToUpdate.first().authenticationProviderId));
    });
    authModel.updateGetProviderDescription(getProviderDescription);
    authModel.updateAuthenticationProviderIds(_.map(siteUsersToUpdate.toArray(), (user: ISiteUser) => user.authenticationProviderId));
    scope.authenticationProviderSelectorProps = {model: authModel};

    const currentUser = siteUsersToUpdate.get(getSessionManagementServiceInstance().sessionUserInfo().id);
    let noChanges = true;
    scope.$watch('authenticationProviderSelectorProps.model.authenticationProvider', (newValue: IAuthenticationProvider, oldValue: IAuthenticationProvider) => {
      noChanges = false;
      scope.isAboutToBeLoggedOut = currentUser && currentUser.authenticationProviderId !== newValue.id;
      scope.$apply();
    });

    const userIds = _.map(siteUsersToUpdate.toArray(), (user: ISiteUser) => user.id);

    const dialog = this.confirmActionDialog.open({
      title: this.$translate.instant('updateUserAuthenticationTitle'),
      bodyTemplateUrl: 'updateUserAuthenticationDialog_v2.html',
      scope: scope,
      position: ModalService.anchoredTo(context.targetElement),
      actionName: this.$translate.instant('update'),
      action: (confirmActionDialogInstance: IConfirmActionDialogInstance) => {
        const provider = scope.authenticationProviderSelectorProps.model.authenticationProvider;
        const updateUserAuthentication = () => {
          const promise = this.siteAuthenticationSettingsService.setProviderIdForUsers(provider.id, userIds);
          this.showNotification(siteUsersToUpdate, promise);
          return promise;
        };
        return this.determineIfNoMoreAdminsUsingDefaultProvider(siteUsersToUpdate, provider).then((showWarning: boolean) => {
          if (showWarning) {
            const warningDialog = this.confirmActionDialog.open({
              title: this.$translate.instant('updateUserAuthenticationTitle'),
              bodyTemplateUrl: 'updateUserAuthenticationDialogWarning.html',
              isDeleteAction: true,
              actionName: this.$translate.instant('update'),
              action: updateUserAuthentication,
            });
            return warningDialog.result;
          } else {
            return updateUserAuthentication();
          }
        });
      },
      confirmDisabled: () => noChanges
    });
    return dialog.result;
  }

  private showNotification(siteUsersToUpdate: Set<ISiteUser>, promise: ng.IPromise<IResult>) {
    promise.catch(() => {
      ContentActionNotification.displayFailureToast(this.$translate.instant('unexpectedError'));
    });
    promise.then((result: IResult) => {
      const multipleItemResult = new MultipleItemResult(siteUsersToUpdate, result);
      ContentActionNotification.displayItemizedToastsForResult(createMultipleItemResultNotificationValues(multipleItemResult,
        {
          describeSuccessSummary: (successCount: number) =>
            this.$translate.instant('updateUserAuthenticationSuccessSummary', {COUNT: successCount}, L10n.Interpolations.Count),

          describeErrorSummary: (errorCount: number) =>
            this.$translate.instant('updateUserAuthenticationErrorSummary', {COUNT: errorCount}, L10n.Interpolations.Count),

          describeErrorDetail: (errorItem: IErrorItem<ISiteUser>) => {
            return {
              itemName: errorItem.item && errorItem.item.displayName,
              itemUrl: null,
              errorMessage: this.getErrorDetailText(errorItem.error)
            };
          }
        }));
    });
  }

  private getErrorDetailText(error: IError) {
    switch(error.code) {
      case CommonErrorCode.SiteSAMLUserExistsOnAnotherSite:
        return this.$translate.instant('userNameExistsOnServerForUpdateAuthenticationToSiteSAML');
      case CommonErrorCode.SystemUserCannotBeSiteSAMLUser:
        return this.$translate.instant('systemAdminNotAllowedAsSiteSAMLUser');
      case CommonErrorCode.GuestUserCannotBeSiteSAMLUser:
        return this.$translate.instant('guestUserNotAllowedAsSiteSAMLUser');
      default:
        return this.$translate.instant('unexpectedError');
    }
  }

  private determineIfNoMoreAdminsUsingDefaultProvider(siteUsersToUpdate: Set<ISiteUser>, newAuthenticationProvider: IAuthenticationProvider): ng.IPromise<boolean> {
    const defaultAuthenticationProviderId = this.siteAuthenticationSettingsService.getDefaultProvider().id;
    const authenticatedByDefaultProvider = (user: ISiteUser) => user.authenticationProviderId === defaultAuthenticationProviderId;

    if (!is3rdParty(newAuthenticationProvider.id)) {
      return this.$q.resolve(false);
    }
    return SiteUsers.instance.getUsersBySiteRoles(SiteRolesResource.instance.getAdminRoles()).then((result: ListResult<ISiteUser>) => {
      let adminsUsingDefaultProviderBefore = Set.fromIdItemArray(result.items).filter(authenticatedByDefaultProvider);
      let adminsUsingDefaultProviderAfter = adminsUsingDefaultProviderBefore.clone();
      adminsUsingDefaultProviderAfter.removeItemsIn(siteUsersToUpdate);
      return !adminsUsingDefaultProviderBefore.isEmpty() && adminsUsingDefaultProviderAfter.isEmpty();
    });
  }
}

Module.instance().service('UpdateUserAuthenticationAction', UpdateUserAuthenticationAction);

interface IDialogScope extends ng.IScope {
  authenticationProviderSelectorProps: AuthenticationProviderSelectorProps;
  isAboutToBeLoggedOut: boolean;
}
